import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { performLogout } from "@/lib/auth/logout";
import { toast } from "sonner";

const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000;
const WARNING_BEFORE_MS = 60 * 1000;
const MOUSEMOVE_THROTTLE_MS = 30 * 1000;
const SESSION_KEEP_ALIVE_MS = 5 * 60 * 1000;

const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "focus",
] as const;

interface InactivityLogoutOptions {
  enabled?: boolean;
}

export function useInactivityLogout(
  options: InactivityLogoutOptions = {},
) {
  const { enabled = true } = options;
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const extendSession = useAuthStore((s) => s.extendSession);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMouseMoveRef = useRef(0);
  const resetTimerRef = useRef<() => void>(() => {});

  const resetTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);

    if (!isAuthenticated) return;

    extendSession();

    warningRef.current = setTimeout(() => {
      toast.warning("1분 후 비활동으로 인해 자동 로그아웃됩니다.", {
        duration: 60000,
        action: {
          label: "연장",
          onClick: () => resetTimerRef.current(),
        },
      });
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    timeoutRef.current = setTimeout(async () => {
      await performLogout();
      toast.error("비활동으로 인해 자동 로그아웃되었습니다.");
      navigate("/login");
    }, INACTIVITY_TIMEOUT_MS);
  }, [isAuthenticated, navigate, extendSession]);

  useEffect(() => {
    resetTimerRef.current = resetTimer;
  }, [resetTimer]);

  // When disabled, keep session alive with periodic extension
  useEffect(() => {
    if (enabled || !isAuthenticated) return;

    extendSession();

    const interval = setInterval(() => {
      extendSession();
    }, SESSION_KEEP_ALIVE_MS);

    return () => clearInterval(interval);
  }, [enabled, isAuthenticated, extendSession]);

  // When enabled, set up activity tracking and logout timers
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const handleMouseMove = () => {
      const now = Date.now();
      if (now - lastMouseMoveRef.current < MOUSEMOVE_THROTTLE_MS) return;
      lastMouseMoveRef.current = now;
      resetTimer();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        resetTimer();
      }
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, resetTimer);
    }
    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    resetTimer();

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, resetTimer);
      }
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [enabled, isAuthenticated, resetTimer]);
}
